import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AutoAwesome, Close, Send } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { ApiError, askTldr } from '../api/blogApi';
import { MAX_QUESTION_CHARS } from '../config/site';

// Near-full-screen on desktop, genuinely full-screen on phones.
const WideDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 880,
    height: '85vh',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      maxHeight: '100%',
      margin: 0,
      borderRadius: 0,
      maxWidth: '100%',
    },
  },
}));

// `theme.shape.borderRadius` is typed `string | number`, so it can't be
// multiplied; spacing(2) gives the same rounded-bubble result.
const Bubble = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
}));

type Exchange = {
  question: string;
  answer?: string;
  error?: string;
};

type TldrDialogProps = {
  open: boolean;
  onClose: () => void;
  blogId: string;
  blogName: string;
  summary?: string;
};

const TldrDialog: React.FC<TldrDialogProps> = ({ open, onClose, blogId, blogName, summary }) => {
  const [question, setQuestion] = useState('');
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [pending, setPending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  // Keep the newest exchange in view as the conversation grows.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [exchanges, pending]);

  const tooLong = question.length > MAX_QUESTION_CHARS;
  const canSend = question.trim() !== '' && !tooLong && !pending;

  const send = async () => {
    if (!canSend) return;
    const asked = question.trim();
    setQuestion('');
    setPending(true);
    setExchanges((prev) => [...prev, { question: asked }]);

    try {
      const { answer } = await askTldr(blogId, asked);
      setExchanges((prev) =>
        prev.map((e, i) => (i === prev.length - 1 ? { ...e, answer } : e)),
      );
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 429
          ? "You've asked a lot of questions recently. Give it a little while."
          : err instanceof ApiError
            ? err.message
            : 'Something went wrong. Please try again.';
      setExchanges((prev) => prev.map((e, i) => (i === prev.length - 1 ? { ...e, error: message } : e)));
    } finally {
      setPending(false);
    }
  };

  // Enter sends; Shift+Enter adds a newline.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  return (
    <WideDialog open={open} onClose={onClose} aria-labelledby="tldr-title">
      <DialogTitle id="tldr-title" sx={{ pr: 7 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesome color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
              TLDR mode
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {blogName}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="Close TLDR mode"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Summary, generated at publish time, so it costs nothing to show. */}
        <Box>
          <Typography variant="overline" color="text.secondary">
            Summary
          </Typography>
          {summary ? (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No summary has been generated for this post yet.
            </Typography>
          )}
        </Box>

        <Divider />

        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Ask about this post
          </Typography>

          {exchanges.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ask anything about this post and you'll get a short answer. Questions are
              answered one at a time, and each is independent, so include any context you need.
            </Typography>
          )}

          <Stack spacing={2} sx={{ mt: 2 }}>
            {exchanges.map((exchange, i) => (
              <Stack spacing={1} key={i}>
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Bubble elevation={2} sx={{ bgcolor: 'action.selected', maxWidth: '80%' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {exchange.question}
                    </Typography>
                  </Bubble>
                </Stack>

                {(exchange.answer || exchange.error) && (
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 30, height: 30 }}>
                      <AutoAwesome sx={{ fontSize: 17 }} />
                    </Avatar>
                    {exchange.error ? (
                      <Alert severity="warning" sx={{ flex: 1 }}>
                        {exchange.error}
                      </Alert>
                    ) : (
                      <Bubble elevation={2} sx={{ maxWidth: '80%' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {exchange.answer}
                        </Typography>
                      </Bubble>
                    )}
                  </Stack>
                )}
              </Stack>
            ))}

            {pending && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 30, height: 30 }}>
                  <AutoAwesome sx={{ fontSize: 17 }} />
                </Avatar>
                <CircularProgress size={18} />
              </Stack>
            )}
          </Stack>

          <div ref={endRef} />
        </Box>
      </DialogContent>

      <Divider />

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask a question about this post…"
          error={tooLong}
          helperText={`${question.length} / ${MAX_QUESTION_CHARS}`}
          slotProps={{
            formHelperText: { sx: { textAlign: 'right', m: 0, mt: 0.5 } },
            input: {
              endAdornment: (
                <IconButton onClick={send} disabled={!canSend} color="primary" aria-label="Send question">
                  <Send />
                </IconButton>
              ),
            },
          }}
        />
      </Box>
    </WideDialog>
  );
};

export default TldrDialog;
